import { FormEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Llm } from '../../../api/Llm';
import { RagListDTO } from '../../../models/LLM';
import { useToasts } from '../../../contexts/toasts';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { accessLevelOptions, formatDate } from '../../../utils';
import { Link, useNavigate } from 'react-router-dom';
import { Flex, Select, Tooltip } from 'antd';
import UserItem from '../../../components/common/UserItem';
import { PagedResults } from '../../../models/generics';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import AppBrowsingToolbar, { Filters } from '../../../components/common/AppBrowsingToolbar';
import { DefaultOptionType } from 'antd/es/select';
import { EventEmitter } from '../../../utils/EventEmitter';
import AppTag from '../../../components/common/AppTag';
import AppLoader from '../../../components/common/AppLoader';

const DEFAULT_FILTERS: Filters = {
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    ),
  },
  'tags': {
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    }
  },
}

function RagsList() {
  const navigate = useNavigate();
  const [rags, setRags] = useState<RagListDTO[]>();
  const [totalRags, setTotalRags] = useState<number>();
  const [error, setError] = useState(false);
  const [latestParams, setLatestParams] = useState<URLSearchParams>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [rag, setRag] = useState<RagListDTO>();
  const [validated, setValidated] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const ragLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "rags"));

  const ragsFilters: Filters = useMemo(() => {
    return {
      ...DEFAULT_FILTERS,
      'search': {
        placeholder: `Search ${ragLabels.singular}...`,
        query: 'freeText',
      },
    }
  }, [ragLabels]);

  const getRags = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Llm.getRags(params)
      .then(
        ({ data }) => {
          setRags((data as PagedResults<RagListDTO>).content);
          setTotalRags((data as PagedResults<RagListDTO>).pageMetadata.totalElements);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rags-error', message, `${ragLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast, ragLabels, latestParams]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setRags(undefined);
    getRags(params);
  }, [getRags]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const handleClose = useCallback(() => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setRag(undefined);
    setValidated(false);
    setEditing(undefined);
  }, []);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleRagEdit = useCallback((rag: RagListDTO, index: number) => () => {
    setEditing(index);
    setRag(rag);
    setShowEditModal(true);
  }, []);

  const handleRagDelete = useCallback((rag: RagListDTO) => () => {
    setRag(rag);
    setShowDeleteModal(true);
  }, []);

  const handleSubmit = useCallback((evt: FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();

    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader();

      const promise = editing !== undefined
        ? Llm.editRag(rag!)
        : Llm.createRag(rag!);

      promise.then(
        ({ data }) => {
          hideLoader();
          showToast('rag-success', `${ragLabels.singular} '${rag!.name}' ${rag!.id ? 'updated' : 'created'} successfully`, `${ragLabels.plural} ${rag!.id ? 'Updated' : 'Created'}`, 'success');
          handleClose();
          navigate(`/llm/rags/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rags-error', message, `${ragLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    }
  }, [rag, navigate, editing, handleClose, showLoader, hideLoader, showToast, ragLabels]);

  const handleFieldChange = useCallback((evt: any) => {
    if (Array.isArray(evt)) {
      setRag(r => ({
        ...r!,
        tags: evt,
      }));
    } else if (typeof evt === 'object') {
      const field = evt.target.name;
      const value = evt.target.value;

      setRag(r => ({
        ...r!,
        [field]: value,
      }));
    } else {
      setRag(r => ({
        ...r!,
        accessLevel: evt,
      }));
    }
  }, []);

  const deleteRag = useCallback(() => {
    showLoader();

    Llm.deleteRag(rag!.id)
      .then(
        () => {
          hideLoader();
          handleClose();
          reset();
          showToast('rag-success', `${ragLabels.singular} deleted successfully`, `${ragLabels.singular} Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-error', message, `${ragLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, rag, reset, showToast, ragLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!rags) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return rags!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {ragLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {rags?.map((rag, index) => (
            <Col key={rag.id} md={3} className="px-2">
              <Card className="shadow-sm mb-3">
                <div className="d-flex p-1">
                  <Badge className="shadow w-auto">{rag.accessLevel}</Badge>
                </div>
                <Card.Body className="py-2">
                  <Card.Title className="mb-1">
                    <Link to={`/llm/rags/${rag.id}`}>{rag.name}</Link>
                  </Card.Title>
                  <Tooltip title={rag.description} mouseEnterDelay={1}>
                    <p className="line-clamp-2 text-secondary mb-2">{rag.description}</p>
                  </Tooltip>

                  <Flex className="tags-wrapper mb-3">
                    {rag.tags.length === 0 && <AppTag noTags />}
                    {rag.tags.map(tag => <AppTag key={tag} tag={tag} />)}
                  </Flex>

                  <Row className="justify-content-center">
                    <Tooltip title={`Edit ${ragLabels.singular}`}>
                      <button className="circle-button dark ms-2" onClick={handleRagEdit(rag, index)}>
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                    </Tooltip>
                    <Tooltip title={`Delete ${ragLabels.singular}`}>
                      <button className="circle-button dark ms-2" onClick={handleRagDelete(rag)}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </Tooltip>
                  </Row>
                </Card.Body>
                <UserItem className="border rounded p-1 m-2" user={rag.owner!} organization={rag.organization!} />
                <small className="ms-1 font-sm text-muted">
                  <b>Created:</b>
                  <span className="ms-1">{formatDate(rag.createDateTime)}</span>
                </small>
              </Card>
            </Col>
          ))}
        </Row>
      );
  }, [rags, handleRagEdit, handleRagDelete, ragLabels, error]);

  return (
    <Container className="mt-3">
      <AppBrowsingToolbar
        title={`${ragLabels.plural} List`}
        actions={
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create {ragLabels.singular}</span>
          </Button>
        }
        total={totalRags}
        filters={ragsFilters}
      />
      <hr />

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? `Edit ${ragLabels.singular}` : `New ${ragLabels.singular}`}</Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="rag-name">
              <Form.Label>Name</Form.Label>
              <Form.Control name="name" placeholder={`${ragLabels.singular} Name`} size="sm" value={rag?.name} onChange={handleFieldChange} required />
              <Form.Control.Feedback type="invalid">{ragLabels.singular} Name is required.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="rag-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" placeholder={`${ragLabels.singular} Description`} size="sm" value={rag?.description} onChange={handleFieldChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Privacy Level</Form.Label>
              <Select
                placeholder="Select privacy level"
                value={rag?.accessLevel}
                allowClear
                onChange={handleFieldChange}
                className="w-100"
              >
                {accessLevelOptions.map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <FontAwesomeIcon icon={option.icon} fixedWidth />
                    <span className="ms-2">{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Select mode="tags" className="w-100" placeholder={`${ragLabels.singular} Tags`} value={rag?.tags} onChange={handleFieldChange} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
            <Button type="submit" variant="success" size="sm">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Save</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {ragLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {ragLabels.singular} <b>{rag?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteRag}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(RagsList);